import React from 'react';
import { motion } from 'framer-motion';
import { Trophy, ShieldCheck, Target, Users, Star, Zap } from 'lucide-react';
import './AboutPage.css';
import SEO from '../components/SEO';

// Update local images for About page
import missionHero from '../Images/mission.jpeg';
import shelf3 from '../Images/shelf 3.jpeg';

const AboutPage = () => {
    const stats = [
        { label: 'Founded', value: '2023' },
        { label: 'Elite Athletes', value: '500+' },
        { label: 'Provincial Selects', value: '15+' },
        { label: 'Locations', value: 'Harare' }
    ];

    const values = [
        { icon: <Target className="text-secondary" />, title: 'Precision', desc: 'Elite training requires scientific precision in every drill and every match.' },
        { icon: <ShieldCheck className="text-secondary" />, title: 'Discipline', desc: 'Promoting life skills and fighting drug abuse through high-performance routines.' },
        { icon: <Trophy className="text-secondary" />, title: 'Excellence', desc: 'Consistent provincial and national representation across all age groups.' },
        { icon: <Users className="text-secondary" />, title: 'Mentorship', desc: 'Developing the leaders of tomorrow on and off the sporting field.' }
    ];

    return (
        <main className="about-page-elite">
            <SEO
                title="About Us"
                description="Learn about Vision Sports Academy's mission to cultivate champions in Harare. Our legacy of excellence, mentorship, and community impact."
            />
            {/* Hero Section */}
            <header className="about-hero" style={{ backgroundImage: `url(${missionHero})`, backgroundSize: 'cover', backgroundPosition: 'center' }}>
                <div className="about-hero__overlay"></div>
                <div className="container about-hero__content">
                    <motion.div
                        initial={{ opacity: 0, y: 30 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.8 }}
                    >
                        <span className="about-hero__tag">Vision Sports Academy</span>
                        <h1 className="about-hero__title">
                            CULTIVATING <span className="text-secondary">CHAMPIONS</span>
                        </h1>
                        <p className="about-hero__subtitle">
                            Harare's premier elite athletic development hub for the next generation of provincial and national stars.
                        </p>
                    </motion.div>
                </div>
            </header>

            {/* Stats Bar */}
            <section className="about-stats">
                <div className="container about-stats__grid">
                    {stats.map((stat, idx) => (
                        <div key={idx} className="about-stat-item">
                            <span className="about-stat-value">{stat.value}</span>
                            <span className="about-stat-label">{stat.label}</span>
                        </div>
                    ))}
                </div>
            </section>

            {/* Our Story Section */}
            <section className="section about-story">
                <div className="container about-story__grid">
                    <motion.div
                        className="about-story__image-container"
                        initial={{ opacity: 0, x: -50 }}
                        whileInView={{ opacity: 1, x: 0 }}
                        viewport={{ once: true }}
                    >
                        <div className="about-story__image-square"></div>
                        <img src={shelf3} alt="Vision Sports Hub" className="about-story__image" />
                    </motion.div>

                    <motion.div
                        className="about-story__content"
                        initial={{ opacity: 0, x: 50 }}
                        whileInView={{ opacity: 1, x: 0 }}
                        viewport={{ once: true }}
                    >
                        <span className="section-tag text-secondary">Our Legacy</span>
                        <h2 className="section-title">A VISION FOR <span className="text-secondary">EXCELLENCE</span></h2>
                        <p className="about-narrative">
                            Founded in 2023, Vision Sports Academy emerged from a deep passion for professional youth sports in Zimbabwe.
                            We recognized a gap in elite coaching and structured pathways, and set out to create a world-class hub at
                            Fife Avenue Mall.
                        </p>
                        <p className="about-narrative">
                            Operating primarily from the ZB Grounds, our programs fuse high-performance technical training
                            with character development. We are more than just a training ground; we are a community-driven
                            force fighting drug abuse and fostering discipline among Harare's youth.
                        </p>
                        <div className="about-highlights">
                            <div className="about-highlight-item">
                                <Zap size={20} className="text-secondary" />
                                <span>100% Zero Tolerance Anti-Drug Policy</span>
                            </div>
                            <div className="about-highlight-item">
                                <Star size={20} className="text-secondary" />
                                <span>Provincial Selections in Hockey & Cricket</span>
                            </div>
                        </div>
                    </motion.div>
                </div>
            </section>

            {/* Our Values */}
            <section className="section about-values bg-dark">
                <div className="container">
                    <div className="text-center mb-16">
                        <span className="section-tag text-secondary">The Core</span>
                        <h2 className="section-title text-white">OUR STRATEGIC <span className="text-secondary">PILLARS</span></h2>
                    </div>
                    <div className="about-values__grid">
                        {values.map((v, idx) => (
                            <motion.div
                                key={idx}
                                className="value-card"
                                initial={{ opacity: 0, y: 20 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true }}
                                transition={{ delay: idx * 0.1 }}
                            >
                                <div className="value-card__icon">{v.icon}</div>
                                <h4 className="value-card__title">{v.title}</h4>
                                <p className="value-card__desc">{v.desc}</p>
                            </motion.div>
                        ))}
                    </div>
                </div>
            </section>

            {/* CTA */}
            <section className="about-cta section">
                <div className="container text-center">
                    <h2 className="about-cta__title">JOIN THE VISION <span className="text-secondary">LEGACY</span></h2>
                    <p className="about-cta__text">Professional coaching for ambitious athletes in Harare.</p>
                    <a
                        href="https://wa.me/263781665264?text=Hi, I would like to register with Vision Sports Academy."
                        target="_blank"
                        rel="noopener noreferrer"
                        className="btn btn-primary btn-large"
                    >
                        Register Now
                    </a>
                </div>
            </section>
        </main>
    );
};

export default AboutPage;
